import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageCircle, LogOut, Search, Flame, Sun, Snowflake, Loader2, X,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAGS = {
  hot: { label: "Hot", color: "#ff3823", icon: Flame },
  warm: { label: "Warm", color: "#e5a93b", icon: Sun },
  cold: { label: "Cold", color: "#3b82f6", icon: Snowflake },
};

const STATUSES = ["new", "contacted", "call_scheduled", "enrolled", "closed"];
const STATUS_LABELS = { new: "New", contacted: "Contacted", call_scheduled: "Call Scheduled", enrolled: "Enrolled", closed: "Closed" };

const truncate = (s, n) => (s && s.length > n ? s.slice(0, n).trimEnd() + "…" : s || "");

const FOLLOWUPS = [
  {
    key: "24h",
    label: "24 hours",
    days: 1,
    msg: (a) => `Hey ${a.name}, just checking in after our conversation.\n\nHave you had a chance to think about everything we discussed?`,
  },
  {
    key: "3d",
    label: "Day 3",
    days: 3,
    msg: (a) => `Hey ${a.name}, wanted to check back in.\n\nYou mentioned that ${a.goal.toLowerCase()}, and what you told me about "${truncate(a.struggle, 80)}", was something you really wanted to change.\n\nJust curious, what's holding you back from getting started?`,
  },
  {
    key: "7d",
    label: "Day 7 · close the loop",
    days: 7,
    msg: (a) => `Hey ${a.name}, I'm going to close the loop on this after today.\n\nIf "${truncate(a.desired_outcome, 80)}" is still something you want to work towards, let me know and we can figure out the next step.`,
  },
];

const daysSince = (iso) => (Date.now() - new Date(iso).getTime()) / 86400000;
const dueFollowup = (a) => {
  if (["enrolled", "closed"].includes(a.status)) return null;
  const sent = a.followups_sent || [];
  return FOLLOWUPS.find((f) => !sent.includes(f.key) && daysSince(a.created_at) >= f.days) || null;
};

const inputCls = "bg-[#0d0d10] border border-white/10 focus:border-[#ccff00] outline-none px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 transition-colors";

const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function CallsPanel({ authHeaders }) {
  const [settings, setSettings] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, b] = await Promise.all([
        axios.get(`${API}/bot/settings`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API}/bot/bookings`, { headers: authHeaders(), withCredentials: true }),
      ]);
      setSettings(s.data);
      setBookings(b.data);
    } catch (e) {
      toast.error("Failed to load call settings");
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleDay = (d) => {
    setSettings((s) => ({ ...s, days: s.days.includes(d) ? s.days.filter((x) => x !== d) : [...s.days, d].sort() }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/bot/settings`, settings, { headers: authHeaders(), withCredentials: true });
      toast.success("Availability saved. The bot will offer these slots.");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const hourLabel = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? "AM" : "PM"}`;

  if (!settings) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#ccff00]" size={28} /></div>;

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="calls-panel">
      <div className="border border-white/10 bg-[#121216] p-6 sm:p-8">
        <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">Bot call availability</p>
        <h2 className="font-display uppercase text-2xl mb-6">Your call windows (IST)</h2>
        <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">Days you take calls</p>
        <div className="flex flex-wrap gap-2 mb-6" data-testid="day-toggles">
          {dayNames.map((d, i) => (
            <button
              key={d}
              data-testid={`day-toggle-${i}`}
              onClick={() => toggleDay(i)}
              className={`px-4 py-2.5 border font-mono-alt text-[10px] tracking-[0.15em] uppercase transition-colors ${
                settings.days.includes(i) ? "border-[#ccff00] bg-[#ccff00]/15 text-white" : "border-white/10 text-zinc-500 hover:text-white"
              }`}
            >
              {d}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div>
            <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">From</p>
            <select
              data-testid="start-hour-select"
              value={settings.start_hour}
              onChange={(e) => setSettings({ ...settings, start_hour: Number(e.target.value) })}
              className="w-full bg-[#0d0d10] border border-white/10 px-3 py-2.5 text-sm text-white outline-none focus:border-[#ccff00]"
            >
              {Array.from({ length: 18 }, (_, i) => i + 5).map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
            </select>
          </div>
          <div>
            <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">To</p>
            <select
              data-testid="end-hour-select"
              value={settings.end_hour}
              onChange={(e) => setSettings({ ...settings, end_hour: Number(e.target.value) })}
              className="w-full bg-[#0d0d10] border border-white/10 px-3 py-2.5 text-sm text-white outline-none focus:border-[#ccff00]"
            >
              {Array.from({ length: 18 }, (_, i) => i + 6).map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
            </select>
          </div>
          <div>
            <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Slot length</p>
            <select
              data-testid="slot-minutes-select"
              value={settings.slot_minutes}
              onChange={(e) => setSettings({ ...settings, slot_minutes: Number(e.target.value) })}
              className="w-full bg-[#0d0d10] border border-white/10 px-3 py-2.5 text-sm text-white outline-none focus:border-[#ccff00]"
            >
              {[20, 30, 45, 60].map((m) => <option key={m} value={m}>{m} min</option>)}
            </select>
          </div>
        </div>
        <button
          data-testid="save-availability-btn"
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 bg-[#ccff00] hover:bg-[#a8d600] disabled:opacity-50 text-black font-mono-alt text-[10px] tracking-[0.2em] uppercase px-6 py-3 transition-colors"
        >
          {saving && <Loader2 size={13} className="animate-spin" />} Save availability
        </button>
      </div>

      <div className="border border-white/10 bg-[#121216] p-6 sm:p-8">
        <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">Booked by the bot</p>
        <h2 className="font-display uppercase text-2xl mb-6">Upcoming calls</h2>
        {bookings.length === 0 ? (
          <p className="text-sm text-zinc-600" data-testid="bookings-empty">No calls booked yet. Once the bot is live, bookings land here and on your WhatsApp.</p>
        ) : (
          <div className="space-y-2" data-testid="bookings-list">
            {bookings.map((b, i) => (
              <div key={b.wa_id} data-testid={`booking-row-${i}`} className="border border-white/10 bg-[#0d0d10] px-5 py-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{b.name} <span className="text-zinc-600 font-normal text-sm">· {b.goal}</span></p>
                  <p className="text-sm text-zinc-500 truncate">{b.phone}</p>
                </div>
                <span className="font-mono-alt text-[10px] tracking-[0.15em] uppercase text-[#e5a93b] whitespace-nowrap">{b.slot_label} IST</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [apps, setApps] = useState(null);
  const [tab, setTab] = useState("leads");
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("all");
  const [notes, setNotes] = useState("");
  const navigate = useNavigate();

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/applications`, { headers: authHeaders(), withCredentials: true });
      setApps(data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
      else toast.error("Failed to load applications");
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const patch = async (id, body) => {
    try {
      const { data } = await axios.patch(`${API}/applications/${id}`, body, { headers: authHeaders(), withCredentials: true });
      setApps((prev) => prev.map((a) => (a.id === id ? data : a)));
      setSelected((prev) => (prev && prev.id === id ? data : prev));
      return data;
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) {}
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  const filtered = useMemo(() => {
    if (!apps) return [];
    return apps.filter((a) => {
      if (tagFilter !== "all" && (a.tag || "untagged") !== tagFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return [a.name, a.goal, a.whatsapp, a.instagram].filter(Boolean).some((v) => v.toLowerCase().includes(q));
      }
      return true;
    });
  }, [apps, search, tagFilter]);

  const counts = useMemo(() => {
    if (!apps) return {};
    return {
      total: apps.length,
      hot: apps.filter((a) => a.tag === "hot").length,
      new: apps.filter((a) => a.status === "new").length,
      enrolled: apps.filter((a) => a.status === "enrolled").length,
    };
  }, [apps]);

  const openApp = (a) => { setSelected(a); setNotes(a.notes || ""); };

  const sendFollowup = async (f) => {
    if (!selected) return;
    const url = `https://wa.me/${String(selected.whatsapp).replace(/\D/g, "")}?text=${encodeURIComponent(f.msg(selected))}`;
    window.open(url, "_blank");
    const sent = Array.from(new Set([...(selected.followups_sent || []), f.key]));
    await patch(selected.id, { followups_sent: sent });
  };

  const waLink = (a) => `https://wa.me/${String(a.whatsapp).replace(/\D/g, "")}?text=${encodeURIComponent(`Hey ${a.name}, Nimish here. Just went through your application. I saw that your main goal is ${a.goal}. Before I suggest anything, I want to understand your situation a little better. What's the biggest thing you feel has stopped you from achieving this so far?`)}`;

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-[#f4f4f6]" data-testid="admin-dashboard">
      <nav className="border-b border-white/10 backdrop-blur-xl bg-black/70 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <span className="flex items-center">
            <img src="/logo.png" alt="Strength Syndicate" className="h-16 w-auto" />
            <span className="ml-3 font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500">Lead Board</span>
          </span>
          <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 font-mono-alt text-[11px] tracking-[0.2em] uppercase text-zinc-500 hover:text-white transition-colors">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex gap-2 mb-8" data-testid="admin-tabs">
          {[["leads", "Leads"], ["calls", "Calls"]].map(([key, label]) => (
            <button
              key={key}
              data-testid={`tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-6 py-3 border font-mono-alt text-[11px] tracking-[0.25em] uppercase transition-colors ${
                tab === key ? "border-[#ccff00] bg-[#ccff00]/15 text-white" : "border-white/10 text-zinc-500 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === "leads" && (<>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10" data-testid="admin-stats">
          {[["Total", counts.total], ["New", counts.new], ["Hot", counts.hot], ["Enrolled", counts.enrolled]].map(([label, val]) => (
            <div key={label} className="border border-white/10 bg-[#121216] p-5" data-testid={`stat-${label.toLowerCase()}`}>
              <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500">{label}</p>
              <p className="font-display text-3xl mt-2">{val ?? ""}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-8">
          <div className="relative">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input
              data-testid="admin-search-input"
              className={`${inputCls} pl-10 w-64`}
              placeholder="Search name, goal, number…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-2" data-testid="admin-tag-filters">
            {["all", "hot", "warm", "cold", "untagged"].map((t) => (
              <button
                key={t}
                data-testid={`filter-${t}`}
                onClick={() => setTagFilter(t)}
                className={`px-4 py-2.5 border font-mono-alt text-[10px] tracking-[0.2em] uppercase transition-colors ${
                  tagFilter === t ? "border-[#ccff00] bg-[#ccff00]/10 text-white" : "border-white/10 text-zinc-500 hover:text-white"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {!apps ? (
          <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#ccff00]" size={28} /></div>
        ) : filtered.length === 0 ? (
          <div className="border border-white/10 bg-[#121216] p-16 text-center" data-testid="admin-empty-state">
            <p className="font-display uppercase text-2xl text-zinc-500">No applications yet</p>
            <p className="mt-3 text-sm text-zinc-600">New applications from the landing page will appear here.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="admin-application-list">
            {filtered.map((a) => (
              <button
                key={a.id}
                data-testid={`application-row-${a.id}`}
                onClick={() => openApp(a)}
                className="w-full text-left grid sm:grid-cols-[1fr_140px_130px_120px_40px] gap-3 items-center border border-white/10 bg-[#121216] hover:border-[#ccff00]/40 px-5 py-4 transition-colors"
              >
                <div className="min-w-0">
                  <p className="font-semibold truncate">{a.name} <span className="text-zinc-600 font-normal text-sm">· {a.age}</span></p>
                  <p className="text-sm text-zinc-500 truncate">{a.goal}</p>
                </div>
                <span
                  className="font-mono-alt text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 w-fit"
                  style={{
                    color: a.tag ? TAGS[a.tag].color : "#71717a",
                    background: a.tag ? `${TAGS[a.tag].color}18` : "rgba(255,255,255,0.05)",
                  }}
                  data-testid={`tag-badge-${a.id}`}
                >
                  {a.tag ? TAGS[a.tag].label : "Untagged"}
                </span>
                <span className="flex items-center gap-2">
                  <span className="font-mono-alt text-[10px] tracking-[0.2em] uppercase text-zinc-400" data-testid={`status-badge-${a.id}`}>
                    {STATUS_LABELS[a.status] || a.status}
                  </span>
                  {dueFollowup(a) && (
                    <span className="font-mono-alt text-[9px] tracking-[0.15em] uppercase text-[#ccff00] bg-[#ccff00]/10 border border-[#ccff00]/30 px-2 py-0.5" data-testid={`followup-due-${a.id}`}>
                      {dueFollowup(a).label} follow-up due
                    </span>
                  )}
                </span>
                <span className="font-mono-alt text-[10px] text-zinc-600">{new Date(a.created_at).toLocaleDateString()}</span>
                <MessageCircle size={15} className="text-zinc-600 justify-self-end" />
              </button>
            ))}
          </div>
        )}
        </>
        )}
        {tab === "calls" && <CallsPanel authHeaders={authHeaders} />}
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="bg-[#121216] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="application-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display uppercase text-2xl tracking-wide">
                  {selected.name} <span className="text-zinc-500 text-lg">· {selected.age}</span>
                </DialogTitle>
              </DialogHeader>

              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-2 text-sm" data-testid="application-detail-body">
                <div><p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">WhatsApp</p><p>{selected.whatsapp}</p></div>
                <div><p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">Instagram</p><p>{selected.instagram || "Not shared"}</p></div>
                <div><p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">Goal</p><p>{selected.goal}</p></div>
                <div><p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">Commitment</p><p>{selected.commitment}/5</p></div>
                <div><p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">Investment ready</p><p>{selected.investment}</p></div>
                <div><p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">Applied</p><p>{new Date(selected.created_at).toLocaleString()}</p></div>
                <div className="sm:col-span-2"><p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">Struggling with</p><p className="text-zinc-300 leading-relaxed">{selected.struggle}</p></div>
                <div className="sm:col-span-2"><p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">Desired outcome</p><p className="text-zinc-300 leading-relaxed">{selected.desired_outcome}</p></div>
                <div className="sm:col-span-2"><p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">Tried before</p><p className="text-zinc-300 leading-relaxed">{selected.tried_before}</p></div>
              </div>

              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">Lead temperature</p>
                <div className="flex gap-2" data-testid="tag-buttons">
                  {Object.entries(TAGS).map(([key, t]) => (
                    <button
                      key={key}
                      data-testid={`tag-${key}-btn`}
                      onClick={() => patch(selected.id, { tag: key })}
                      className={`flex items-center gap-2 px-4 py-2.5 border font-mono-alt text-[10px] tracking-[0.2em] uppercase transition-colors ${
                        selected.tag === key ? "text-white" : "text-zinc-500 hover:text-white border-white/10"
                      }`}
                      style={selected.tag === key ? { borderColor: t.color, background: `${t.color}22` } : {}}
                    >
                      <t.icon size={13} style={{ color: t.color }} /> {t.label}
                    </button>
                  ))}
                  {selected.tag && (
                    <button data-testid="tag-clear-btn" onClick={() => patch(selected.id, { tag: "" })} className="px-3 py-2.5 border border-white/10 text-zinc-500 hover:text-white transition-colors">
                      <X size={13} />
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-6">
                <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">Follow-up status</p>
                <div className="flex flex-wrap gap-2" data-testid="status-buttons">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      data-testid={`status-${s}-btn`}
                      onClick={() => patch(selected.id, { status: s })}
                      className={`px-4 py-2.5 border font-mono-alt text-[10px] tracking-[0.2em] uppercase transition-colors ${
                        selected.status === s ? "border-[#ccff00] bg-[#ccff00]/15 text-white" : "border-white/10 text-zinc-500 hover:text-white"
                      }`}
                    >
                      {STATUS_LABELS[s]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">WhatsApp follow-up scripts</p>
                <div className="space-y-3" data-testid="followup-scripts">
                  {FOLLOWUPS.map((f) => {
                    const sent = (selected.followups_sent || []).includes(f.key);
                    const due = !sent && daysSince(selected.created_at) >= f.days && !["enrolled", "closed"].includes(selected.status);
                    return (
                      <div
                        key={f.key}
                        data-testid={`followup-${f.key}`}
                        className={`border p-4 transition-colors ${due ? "border-[#ccff00]/50 bg-[#ccff00]/5" : "border-white/10 bg-[#0d0d10]"}`}
                      >
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <span className="font-mono-alt text-[10px] tracking-[0.2em] uppercase text-zinc-400">{f.label}</span>
                          {sent ? (
                            <span className="font-mono-alt text-[9px] tracking-[0.15em] uppercase text-[#25d366]" data-testid={`followup-sent-${f.key}`}>Sent ✓</span>
                          ) : due ? (
                            <span className="font-mono-alt text-[9px] tracking-[0.15em] uppercase text-[#ccff00] animate-pulse-dot" data-testid={`followup-due-badge-${f.key}`}>Due now</span>
                          ) : (
                            <span className="font-mono-alt text-[9px] tracking-[0.15em] uppercase text-zinc-600">Not due yet</span>
                          )}
                        </div>
                        <p className="text-xs text-zinc-500 leading-relaxed whitespace-pre-line mb-3">{f.msg(selected)}</p>
                        <button
                          data-testid={`followup-send-${f.key}`}
                          onClick={() => sendFollowup(f)}
                          className="flex items-center gap-2 px-4 py-2 bg-[#25d366]/15 border border-[#25d366]/40 text-[#25d366] hover:bg-[#25d366]/25 font-mono-alt text-[10px] tracking-[0.2em] uppercase transition-colors"
                        >
                          <MessageCircle size={12} /> {sent ? "Resend" : "Send on WhatsApp"}
                        </button>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-3 text-[11px] text-zinc-600">After day 7 with no reply, stop chasing. Mark the lead closed.</p>
              </div>

              <div className="mt-6">
                <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">Notes</p>
                <textarea
                  data-testid="notes-textarea"
                  rows={3}
                  className={`${inputCls} w-full`}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Call notes, follow-up reminders…"
                />
                <button
                  data-testid="save-notes-btn"
                  onClick={async () => { const r = await patch(selected.id, { notes }); if (r) toast.success("Notes saved"); }}
                  className="mt-3 px-6 py-2.5 bg-white/10 hover:bg-white/15 font-mono-alt text-[10px] tracking-[0.2em] uppercase transition-colors"
                >
                  Save notes
                </button>
              </div>

              <a
                href={waLink(selected)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-whatsapp-btn"
                className="mt-6 flex items-center justify-center gap-3 bg-[#25d366] hover:bg-[#1ebe57] text-black font-mono-alt text-xs tracking-[0.2em] uppercase px-8 py-4 transition-colors"
              >
                <MessageCircle size={16} /> Open WhatsApp conversation
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
